/**
 * StepRow — one row per workflow step.
 *
 * Default body is generic (notes + attachments). When `step.detail` is
 * set, the matching rich panel (tech spec, manufacturers, quotes,
 * tracking, customs) is rendered instead of/alongside the generic body.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, Circle, AlertCircle, MoreVertical, Paperclip, MessageSquare,
  ChevronDown, User, Truck, Factory, Building2, Globe2,
} from 'lucide-react';
import { useManifest } from '../ManifestContext';
import TechSpecPanel from './TechSpecPanel';
import ManufacturerTable from './ManufacturerTable';
import OutreachPanel from './OutreachPanel';
import QuoteComparison from './QuoteComparison';
import ContractDocsPanel from './ContractDocsPanel';
import FreightQuotePanel from './FreightQuotePanel';
import TrackingPanel from './TrackingPanel';
import CustomsChecklist from './CustomsChecklist';

const OWNER_ICON = {
  Us:        <User size={12} />,
  Factory:   <Factory size={12} />,
  Freighter: <Truck size={12} />,
  Customs:   <Building2 size={12} />,
  Domestic:  <Truck size={12} />,
  Government: <Building2 size={12} />,
};

const STATUS_ICON = {
  done:        <CheckCircle2 size={20} className="step-status-icon done" />,
  in_progress: <Circle size={20} className="step-status-icon in-progress" />,
  pending:     <Circle size={20} className="step-status-icon pending" />,
  blocked:     <AlertCircle size={20} className="step-status-icon blocked" />,
};

const renderRichPanel = (step, order) => {
  switch (step.detail) {
    case 'tech_spec':     return <TechSpecPanel order={order} />;
    case 'manufacturers': return <ManufacturerTable order={order} />;
    case 'outreach':      return <OutreachPanel order={order} />;
    case 'quotes':        return <QuoteComparison order={order} />;
    case 'contract_docs': return <ContractDocsPanel order={order} />;
    case 'freight_quote': return <FreightQuotePanel order={order} />;
    case 'tracking':      return <TrackingPanel order={order} />;
    case 'customs':       return <CustomsChecklist order={order} />;
    default:              return null;
  }
};

const StepRow = ({ step, defaultExpanded = false }) => {
  const { activeOrder, toggleStepDone, setStepState } = useManifest();
  const stateObj = activeOrder.stepStates?.[step.id] || { status: 'pending', notes: '', attachments: [] };
  const [open, setOpen] = useState(defaultExpanded || stateObj.status === 'in_progress');
  const [notes, setNotes] = useState(stateObj.notes || '');
  const debounceRef = useRef(null);

  useEffect(() => { setNotes(stateObj.notes || ''); }, [stateObj.notes]);

  const handleNotes = (val) => {
    setNotes(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setStepState(step.id, { notes: val });
    }, 800);
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    toggleStepDone(step.id);
  };

  const richPanel = renderRichPanel(step, activeOrder);
  const statusKey = stateObj.status || 'pending';
  const attachmentCount = (stateObj.attachments || []).length;
  const ownerIcon = OWNER_ICON[step.owner] || <Globe2 size={12} />;

  return (
    <div className={`manifest-step-row status-${statusKey} ${open ? 'open' : ''}`}>
      <div className="manifest-step-row-head" onClick={() => setOpen((o) => !o)}>
        <button
          type="button"
          className="manifest-step-toggle"
          onClick={handleToggle}
          title={statusKey === 'done' ? 'Mark not done' : 'Mark done'}
        >
          {STATUS_ICON[statusKey]}
        </button>

        <div className="manifest-step-main">
          <div className="manifest-step-title">
            <span className="manifest-step-num">Step {step.id}</span>
            <span className="manifest-step-text">{step.title}</span>
          </div>
          <div className="manifest-step-meta">
            <span className="manifest-step-day">Day {step.targetDay}</span>
            <span className="manifest-step-owner">{ownerIcon} {step.owner}</span>
            {attachmentCount > 0 && (
              <span className="manifest-step-attach"><Paperclip size={11} /> {attachmentCount}</span>
            )}
            {(stateObj.notes && stateObj.notes.trim().length > 0) && (
              <span className="manifest-step-noteflag"><MessageSquare size={11} /></span>
            )}
            {step.detail && <span className="manifest-step-rich-flag">Rich detail</span>}
          </div>
        </div>

        <div className="manifest-step-actions">
          <button
            type="button"
            className="manifest-icon-btn"
            onClick={(e) => { e.stopPropagation(); /* placeholder for "..." menu */ }}
            title="More actions"
          >
            <MoreVertical size={14} />
          </button>
          <ChevronDown size={16} className={`manifest-step-chevron ${open ? 'open' : ''}`} />
        </div>
      </div>

      {open && (
        <div className="manifest-step-body">
          {richPanel}
          <div className="manifest-step-row-grid">
            <div className="manifest-step-field">
              <label className="manifest-step-field-label">Notes</label>
              <textarea
                className="manifest-step-textarea"
                rows={3}
                placeholder="Add notes for this step…"
                value={notes}
                onChange={(e) => handleNotes(e.target.value)}
              />
            </div>
            <div className="manifest-step-field">
              <label className="manifest-step-field-label">Attachments</label>
              <div className="manifest-step-attach-list">
                {(stateObj.attachments || []).map((a, i) => (
                  <div key={i} className="manifest-attach-chip">
                    <Paperclip size={11} /> {a.name}
                  </div>
                ))}
                <button
                  className="manifest-attach-add"
                  onClick={() => setStepState(step.id, {
                    attachments: [...(stateObj.attachments || []), { name: 'demo-attachment.pdf', size: 245_000 }],
                  })}
                >
                  + Attach (demo)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepRow;
